import { PaymentMethodEnum } from '@/types/common';
import type { BootCheckoutConfiguration } from '@/types/boot';

/**
 * The PRD-scoped set of payment methods the `PREFERRED_PAYMENT_METHOD`
 * template (returning-depositor "last-method" branch) is allowed to
 * surface as the sole primary CTA.
 *
 * Source of truth — kept here rather than inlined so:
 *   - {@link PreferredPaymentTemplate.isEligible} (renderability guard)
 *   - any future renderer/whitelist (e.g. analytics, BE drift alerts)
 * stay in lockstep, and unit tests can import the same set without
 * mirroring the list.
 *
 * NOT the same as `bootData.checkoutConfiguration.availablePaymentMethods`
 * — that allow-list governs `SINGLE_PAYMENT_METHOD`. This set is the
 * tighter "we have a renderer for this AND product approved surfacing it
 * one-tap" cut.
 *
 * CARD renders the new-card form as the primary CTA whether or not the
 * user has a saved card (with the "Use other payment method" escape
 * hatch below) — there is no saved-card gate.
 *
 * ACDEV-26312 — added AMAZON_PAY / BLIK / BLIK_CHECKOUT / PIX. All four
 * reuse the existing renderers (`StripeGenericPayForm` for Amazon Pay +
 * PIX, `BlikPayForm` for both BLIK variants — `BLIK_CHECKOUT` is BLIK
 * over Stripe's P24 PaymentMethod, routed inside `BlikPayForm`) so no
 * new form components are required; the template just needs to know
 * they're renderable as a primary CTA. The "Amazon Pay unavailable for
 * browser/currency" and "BLIK/PIX not enabled for publisher" edge cases
 * both surface as the method being absent from BE `paymentMethods` and
 * fall back to DEFAULT via the existing `isPaymentMethodAvailable` gate
 * in {@link PreferredPaymentTemplate.isLastMethodEligible} (with a
 * `logWarn`).
 *
 * Methods deliberately excluded for now: klarna/afterpay/etc. (BNPL UX
 * outside the scope of the lastMethodOnly experiment).
 */
export const SUPPORTED_LAST_PAYMENT_METHODS: ReadonlySet<PaymentMethodEnum> = new Set([
  PaymentMethodEnum.APPLE_PAY,
  PaymentMethodEnum.GOOGLE_PAY,
  PaymentMethodEnum.PAYPAL,
  PaymentMethodEnum.LINK,
  PaymentMethodEnum.CREDIT_CARD,
  PaymentMethodEnum.AMAZON_PAY,
  PaymentMethodEnum.BLIK,
  PaymentMethodEnum.BLIK_CHECKOUT,
  PaymentMethodEnum.PIX,
]);

/**
 * Local APMs the FTD (first-time depositor) branch of the
 * `PREFERRED_PAYMENT_METHOD` template may surface as its single primary CTA,
 * on top of the device's native express wallet.
 *
 * BLIK and PIX used to be reachable only through the returning-depositor
 * "last-method" branch (see {@link SUPPORTED_LAST_PAYMENT_METHODS}), so a
 * first-time depositor in Poland / Brazil never got the local APM their market
 * actually pays with. This set closes that gap, scoped to the two methods
 * product approved: both reuse existing renderers (`BlikPayForm` for either
 * BLIK variant, `StripeGenericPayForm` for PIX), so nothing new has to render.
 *
 * `BLIK_CHECKOUT` (BLIK over Stripe's P24 PaymentMethod) rides along because
 * it is BLIK — the same form dispatches it, and the last-method branch already
 * treats the two variants as one method.
 *
 * Deliberately NOT the whole of {@link SUPPORTED_LAST_PAYMENT_METHODS}: UPI is
 * out of scope (too many input fields for a one-tap FTD CTA), and card / wallet
 * / BNPL FTD behaviour is unchanged.
 */
export const SUPPORTED_FTD_LOCAL_APM_METHODS: ReadonlySet<PaymentMethodEnum> = new Set([
  PaymentMethodEnum.BLIK,
  PaymentMethodEnum.BLIK_CHECKOUT,
  PaymentMethodEnum.PIX,
]);

/**
 * Type guard: `method` is a local APM the FTD branch may surface one-tap
 * (i.e. present in {@link SUPPORTED_FTD_LOCAL_APM_METHODS}).
 *
 * Membership only — {@link PreferredPaymentTemplate} still requires the method
 * to be present in the BE-returned `paymentMethods` before selecting it.
 */
export const isFtdLocalApmMethod = (method: string | null | undefined): method is PaymentMethodEnum =>
  method != null && SUPPORTED_FTD_LOCAL_APM_METHODS.has(method as PaymentMethodEnum);

/**
 * Subset of {@link SUPPORTED_LAST_PAYMENT_METHODS} that the
 * `CHECKOUT_DRAWER_FOR_LAST_METHOD` bottom-sheet is allowed to present.
 *
 * The PREFERRED template's eligibility (which methods it will surface AT
 * ALL) still uses the wider {@link SUPPORTED_LAST_PAYMENT_METHODS} — this
 * set only decides "sheet vs modal" chrome. Methods present in the wider
 * set but absent here (LINK / AMAZON_PAY) still render inside PREFERRED,
 * just inside the legacy full-screen modal.
 *
 * ACDEV-27557 — added the local APMs (BLIK / BLIK_CHECKOUT / PIX) so the
 * sheet also covers the market a first-time depositor was nominated for
 * (see {@link SUPPORTED_FTD_LOCAL_APM_METHODS}) and the returning shopper
 * whose last method was one of them. Both reuse the same one-tap CTA the
 * sheet already renders (`BlikPayForm` / `StripeGenericPayForm`), so the
 * only thing changing is the chrome.
 */
export const DRAWER_SUPPORTED_PAYMENT_METHODS: ReadonlySet<PaymentMethodEnum> = new Set([
  PaymentMethodEnum.APPLE_PAY,
  PaymentMethodEnum.GOOGLE_PAY,
  PaymentMethodEnum.PAYPAL,
  PaymentMethodEnum.CREDIT_CARD,
  PaymentMethodEnum.BLIK,
  PaymentMethodEnum.BLIK_CHECKOUT,
  PaymentMethodEnum.PIX,
]);

/**
 * Type guard: the BE-supplied last-used `preferredPaymentMethod` is eligible
 * for the swipe-up sheet presentation (i.e. present in
 * {@link DRAWER_SUPPORTED_PAYMENT_METHODS}).
 *
 * Single source of truth shared by `PreferredPaymentView`'s `useDrawer` gate
 * and the boot skeleton's drawer prediction so the two can't drift.
 */
export const isDrawerSupportedPaymentMethod = (method: string | null | undefined): method is PaymentMethodEnum =>
  method != null && DRAWER_SUPPORTED_PAYMENT_METHODS.has(method as PaymentMethodEnum);

/**
 * First-time depositor per the BE-populated boot config. Boot-only (no
 * `paymentMethods` / `checkoutData`), so it's safe to call at boot-skeleton
 * time as well as during template eligibility.
 */
export const isFirstTimeDepositor = (cfg: BootCheckoutConfiguration | undefined): boolean =>
  cfg?.isFirstTimeDepositor === true;

/**
 * Type guard: the BE-supplied last-used `preferredPaymentMethod` is one we're
 * allowed to surface one-tap (i.e. present in {@link SUPPORTED_LAST_PAYMENT_METHODS}).
 *
 * Single source of truth for the "is this a renderable last method" cut, shared
 * by {@link PreferredPaymentTemplate}'s eligibility guard and the boot skeleton's
 * drawer prediction so the two can't drift. This is only the membership check —
 * the template still layers its `paymentMethods` / saved-card guards on top.
 */
export const isSupportedLastPaymentMethod = (method: string | null | undefined): method is PaymentMethodEnum =>
  method != null && SUPPORTED_LAST_PAYMENT_METHODS.has(method as PaymentMethodEnum);
